/**
 * Cascadia GameScreen — multiplayer entry point.
 *
 * Lazy-loaded by game-registry.ts. Follows the yinsh/GameScreen.tsx house pattern:
 *   - useGameEventsWS for real-time events
 *   - initializeAsHost / initializeAsGuest on mount
 *   - applyRemoteEvent with dedup via lastAppliedSeq
 *   - onReconnect triggers replayEvents
 *   - Reset on unmount
 *
 * Import cascadia-tokens.css here so tokens are available for all child components.
 */

import { useEffect, useRef, useState } from "react";
import { navigateTo } from "../../router";
import { useGameEventsWS } from "../../shared/hooks/use-game-events-ws";
import { useAuth } from "../../shared/store/auth-store";
import { useLobby, useLobbyActions } from "../../shared/store/lobby-store";
import { getParticipantDisplayNames } from "../../shared/utils/participants";
import { ActionLog } from "./components/ActionLog";
import { Board } from "./components/Board";
import { EndOfGameSheet } from "./components/EndOfGameSheet";
import { LandmarkPanel } from "./components/LandmarkPanel";
import { Market } from "./components/Market";
import { NatureTokenTray } from "./components/NatureTokenTray";
import { PhaseBanner } from "./components/PhaseBanner";
import { PlayersPanel } from "./components/PlayersPanel";
import { ScoringCardPanel } from "./components/ScoringCardPanel";
import { TopBar } from "./components/TopBar";
import { ValidationToast } from "./components/ValidationToast";
import {
  cascadiaMultiplayerActions,
  useCascadiaMultiplayerStore,
} from "./multiplayer-game-store";
import "./styles/cascadia-tokens.css";
import {
  type AxialCoord,
  type Habitat,
  type MarketSlotIndex,
  resolveTileDefId,
  TILE_DEFS_BY_ID,
} from "@cloudnest/cascadia-engine";

export function GameScreen() {
  const { user } = useAuth();
  const { currentRoom } = useLobby();
  const lobbyActions = useLobbyActions();
  const { gameState, history, lastError, myPlayerId } =
    useCascadiaMultiplayerStore();
  const actions = cascadiaMultiplayerActions;

  const storedRoomId = currentRoom?.id ?? null;
  const storedIsHost = currentRoom?.hostUserId === user?.id;

  const actionsRef = useRef(actions);
  const lobbyActionsRef = useRef(lobbyActions);
  useEffect(() => {
    actionsRef.current = actions;
    lobbyActionsRef.current = lobbyActions;
  }, [lobbyActions]);

  const { connectionStatus } = useGameEventsWS(storedRoomId, {
    onGameEvent: (eventType, payload, seq) => {
      actionsRef.current.applyRemoteEvent(eventType, payload, seq);
    },
    onRoomUpdated: (room) => {
      lobbyActionsRef.current.updateCurrentRoom(room);
    },
    onReconnect: () => {
      if (storedRoomId) void actionsRef.current.replayEvents(storedRoomId);
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: storedRoomId captured once at mount; every game:room_updated creates a new object reference that would re-run init and wipe in-progress state.
  useEffect(() => {
    if (!storedRoomId) return;
    if (storedIsHost) {
      const playerIds = currentRoom?.participantUserIds ?? [];
      const playerNames = getParticipantDisplayNames(currentRoom, "Player");
      void actions.initializeAsHost(storedRoomId, playerIds, playerNames);
    } else {
      void actions.initializeAsGuest(storedRoomId);
    }
    return () => {
      actions.reset();
    };
  }, [storedRoomId, storedIsHost]);

  // Local UI state
  const [selectedSlot, setSelectedSlot] = useState<number | null>(null);
  // Drafted-tile rotation 0..5 for the next PlaceTile (free rotation, §583).
  const [tileRotation, setTileRotation] = useState(0);
  // Wireframe §"02 In-match" (lines 287, 292): the player taps an empty
  // adjacent coord to *preview* placement before [ 확정 / Confirm ] commits
  // the PlaceTile action. The pending coord is local — never dispatched
  // until Confirm — so the player can move the ghost or cancel.
  const [pendingPlacementCoord, setPendingPlacementCoord] =
    useState<AxialCoord | null>(null);
  const [selectedLandmarkHabitat, setSelectedLandmarkHabitat] =
    useState<Habitat | null>(null);
  const [phaseBannerMsg, _setPhaseBannerMsg] = useState<string | null>(null);

  // Tile-rotation keyboard shortcuts ([ ccw / ] cw). Registered once
  // (before the loading early-return → rules-of-hooks safe); the latch
  // ref is updated each render once turn/isMyTurn are known.
  const rotKbEnabled = useRef(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!rotKbEnabled.current) return;
      if (e.key === "]") setTileRotation((r) => (r + 1) % 6);
      else if (e.key === "[") setTileRotation((r) => (r + 5) % 6);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (!gameState) {
    return (
      <div
        data-testid="game-loading"
        style={{
          padding: 24,
          textAlign: "center",
          color: "var(--cn-text-secondary)",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <span>Loading game…</span>
      </div>
    );
  }

  const { turn, players, market, scoringCards, ruleset } = gameState;
  const activeSeat = turn.activeSeat;
  const activePlayer = players[activeSeat];

  // Determine which seat index this user occupies (null if spectator)
  const myPlayerIndex = myPlayerId
    ? players.findIndex((p) => p.playerId === myPlayerId)
    : null;
  const isMyTurn = myPlayerIndex !== null && myPlayerIndex === activeSeat;
  rotKbEnabled.current = isMyTurn && turn.phase === "placeTile";

  const activePlayerNatureTokens = activePlayer?.natureTokens ?? 0;
  // draftedWildlife species requires token-instance→species lookup (out of scope for PR 5).
  // Board renders without placement highlights; null is safe — prop is optional.
  const draftedWildlife = null;

  // Drafted tile in hand (between marketCheck and placeTile) — used for the
  // ghost preview hex during the placeTile phase so the player sees the
  // exact tile + current rotation before Confirm.
  const draftedTileInstanceId = activePlayer?.draft.tileInstanceId ?? null;
  const ghostTileDef = draftedTileInstanceId
    ? TILE_DEFS_BY_ID[resolveTileDefId(draftedTileInstanceId)]
    : undefined;
  const ghostRotation = (tileRotation % 6) as 0 | 1 | 2 | 3 | 4 | 5;

  function handleLeave() {
    actions.reset();
    navigateTo("/cascadia/lobby");
  }

  function handleBackToLobby() {
    actions.reset();
    navigateTo("/cascadia/lobby");
  }

  function handleTapCoord(coord: AxialCoord) {
    if (!isMyTurn || !gameState) return;
    const phase = turn.phase;

    if (phase === "placeTile") {
      // Wireframe §"02 In-match" line 292: tap → ghost preview, tap a
      // different cell to move the ghost, [ 확정 ] commits. Toggle off if
      // the same cell is tapped twice.
      setPendingPlacementCoord((cur) =>
        cur && cur.q === coord.q && cur.r === coord.r ? null : coord,
      );
    } else if (phase === "placeWildlife") {
      const action = {
        type: "PlaceWildlife" as const,
        coord,
        stateVersion: gameState.version,
      };
      void actions.submitAction(action);
    }
  }

  // Wireframe docs/wireframes/board-game-client/cascadia.md §"02 In-match"
  // (lines 287–288): tapping a market slot sets a pending selection;
  // [ 확정 / Confirm ] dispatches DraftPair. This is a single-select toggle —
  // tapping the same slot again clears the pending selection.
  function handleSelectDraftSlot(slot: number) {
    if (!isMyTurn || turn.phase !== "marketCheck") return;
    setSelectedSlot((cur) => (cur === slot ? null : slot));
  }

  function handleConfirmDraft() {
    if (!isMyTurn || !gameState || selectedSlot === null) return;
    if (turn.phase !== "marketCheck") return;
    const action = {
      type: "DraftPair" as const,
      slot: selectedSlot as MarketSlotIndex,
      stateVersion: gameState.version,
    };
    void actions.submitAction(action).then(() => setSelectedSlot(null));
  }

  function handleConfirmPlacement() {
    if (!isMyTurn || !gameState || pendingPlacementCoord === null) return;
    if (turn.phase !== "placeTile") return;
    const action = {
      type: "PlaceTile" as const,
      coord: pendingPlacementCoord,
      rotation: (tileRotation % 6) as 0 | 1 | 2 | 3 | 4 | 5,
      stateVersion: gameState.version,
    };
    void actions.submitAction(action).then(() => {
      setPendingPlacementCoord(null);
      setTileRotation(0);
    });
  }

  function handleCancelDraft() {
    setSelectedSlot(null);
    setPendingPlacementCoord(null);
  }

  function handleWipeOptional() {
    if (!isMyTurn || !gameState) return;
    void actions.submitAction({
      type: "WipeMarketOptional",
      stateVersion: gameState.version,
    });
  }

  function handleSpendNatureDecouple() {
    if (!isMyTurn || !gameState) return;
    void actions.submitAction({
      type: "SpendNatureDecouple",
      stateVersion: gameState.version,
    });
  }

  function handleReturnWildlife() {
    if (!isMyTurn || !gameState) return;
    void actions.submitAction({
      type: "ReturnWildlife",
      stateVersion: gameState.version,
    });
  }

  function handleUnlockLandmark(habitat: Habitat) {
    if (!isMyTurn || !gameState) return;
    setSelectedLandmarkHabitat(habitat);
    void actions.submitAction({
      type: "UnlockLandmark",
      habitat,
      stateVersion: gameState.version,
    });
  }

  function handleChooseLandmarkCard(cardId: string) {
    if (!isMyTurn || !gameState) return;
    setSelectedLandmarkHabitat(null);
    void actions.submitAction({
      type: "ChooseLandmarkCard",
      cardId,
      stateVersion: gameState.version,
    });
  }

  function handleSkipLandmark() {
    if (!isMyTurn || !gameState) return;
    setSelectedLandmarkHabitat(null);
    void actions.submitAction({
      type: "SkipLandmark",
      stateVersion: gameState.version,
    });
  }

  function handleDismissError() {
    actions.clearError();
  }

  const isGameOver = gameState.winners !== null;
  const activeBoard = activePlayer?.board ?? { cells: {} };

  // Eligible habitats come directly from the engine's actionBudget (spec §9.1)
  const eligibleHabitats: Habitat[] =
    turn.phase === "unlockLandmark"
      ? [...turn.actionBudget.landmarkEligibleHabitats]
      : [];

  return (
    <div
      data-testid="game-screen"
      style={{
        minHeight: "100vh",
        background: "var(--cn-cascadia-bg)",
        color: "var(--cn-cascadia-label)",
        overflowX: "hidden",
        boxSizing: "border-box",
      }}
    >
      <TopBar
        phase={turn.phase}
        activeSeat={activeSeat}
        totalPlayers={players.length}
        connectionStatus={connectionStatus}
        onLeave={handleLeave}
      />

      <PhaseBanner message={phaseBannerMsg} />

      <ValidationToast lastError={lastError} onDismiss={handleDismissError} />

      <div
        className="cascadia-game-layout"
        style={{
          display: "grid",
          gridTemplateColumns:
            "minmax(0, 240px) minmax(0, 1fr) minmax(0, 280px)",
          gap: 24,
          padding: 24,
          maxWidth: 1280,
          margin: "0 auto",
          alignItems: "start",
          boxSizing: "border-box",
          width: "100%",
        }}
      >
        {/* Left rail: players + action log */}
        <aside style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <PlayersPanel
            players={players}
            activeSeat={activeSeat}
            myPlayerIndex={myPlayerIndex ?? null}
          />
          <ActionLog history={history} />
        </aside>

        {/* Center: board */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 12,
            minWidth: 0,
          }}
        >
          <div
            data-testid="board-frame"
            style={{
              background: "var(--cn-cascadia-tile-bg)",
              border: "1px solid var(--cn-cascadia-market-slot-border)",
              borderRadius: 8,
              padding: 12,
              overflow: "hidden",
            }}
          >
            <Board
              board={activeBoard}
              phase={turn.phase}
              draftedWildlife={draftedWildlife}
              pendingPlacementCoord={pendingPlacementCoord}
              ghostTileDef={ghostTileDef}
              ghostRotation={ghostRotation}
              onTapCoord={handleTapCoord}
              disabled={!isMyTurn}
            />
            {isMyTurn && turn.phase === "placeTile" ? (
              <div
                data-testid="cascadia-rotate-control"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 10,
                  marginTop: 8,
                }}
              >
                <button
                  type="button"
                  aria-label="Rotate counter-clockwise"
                  data-testid="cascadia-rotate-ccw"
                  onClick={() => setTileRotation((r) => (r + 5) % 6)}
                  style={{
                    padding: "4px 12px",
                    borderRadius: 8,
                    border: "1px solid var(--cn-cascadia-tile-stroke)",
                    background: "var(--cn-cascadia-card-bg)",
                    color: "var(--cn-cascadia-text)",
                    cursor: "pointer",
                  }}
                >
                  ↺
                </button>
                <span
                  data-testid="cascadia-rotate-badge"
                  style={{ fontSize: 13, color: "var(--cn-cascadia-text)" }}
                >
                  ↻ {tileRotation}/5
                </span>
                <button
                  type="button"
                  aria-label="Rotate clockwise"
                  data-testid="cascadia-rotate-cw"
                  onClick={() => setTileRotation((r) => (r + 1) % 6)}
                  style={{
                    padding: "4px 12px",
                    borderRadius: 8,
                    border: "1px solid var(--cn-cascadia-tile-stroke)",
                    background: "var(--cn-cascadia-card-bg)",
                    color: "var(--cn-cascadia-text)",
                    cursor: "pointer",
                  }}
                >
                  ↻
                </button>
              </div>
            ) : null}
          </div>

          {/* Nature tray pinned below board */}
          {activePlayer ? (
            <NatureTokenTray
              count={activePlayer.natureTokens}
              spentThisTurn={0}
              estimatedScore={activePlayer.scoreBreakdown?.total ?? null}
            />
          ) : null}

          {/* Draft / placement confirm + cancel — wireframe §"02 In-match"
              two-step flow for both marketCheck (DraftPair) and placeTile
              (PlaceTile) phases. Confirm dispatches the appropriate engine
              action; Cancel clears both pending selections. */}
          {isMyTurn &&
          ((turn.phase === "marketCheck" && selectedSlot !== null) ||
            (turn.phase === "placeTile" && pendingPlacementCoord !== null)) ? (
            <div style={{ display: "flex", gap: 8, padding: "0 12px" }}>
              <button
                data-testid="cascadia-confirm-btn"
                data-action-type={
                  turn.phase === "marketCheck" ? "DraftPair" : "PlaceTile"
                }
                onClick={
                  turn.phase === "marketCheck"
                    ? handleConfirmDraft
                    : handleConfirmPlacement
                }
                type="button"
                style={{
                  minHeight: 44,
                  padding: "10px 20px",
                  borderRadius: 8,
                  border: "1px solid var(--cn-cascadia-selection)",
                  background: "var(--cn-cascadia-phase-bg)",
                  color: "var(--cn-cascadia-phase-text)",
                  cursor: "pointer",
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                확정 / Confirm
              </button>
              <button
                data-testid="cascadia-cancel-btn"
                onClick={handleCancelDraft}
                type="button"
                style={{
                  minHeight: 44,
                  padding: "10px 20px",
                  borderRadius: 8,
                  border: "1px solid var(--cn-cascadia-tile-outline)",
                  background: "transparent",
                  color: "var(--cn-cascadia-label)",
                  cursor: "pointer",
                  fontSize: 13,
                }}
              >
                취소 / Cancel
              </button>
            </div>
          ) : null}

          {/* Action footer for mobile / small screens */}
          {isMyTurn && turn.phase === "placeWildlife" ? (
            <div style={{ display: "flex", gap: 8, padding: "0 12px" }}>
              <button
                data-testid="cascadia-skip-wildlife-btn"
                onClick={handleReturnWildlife}
                type="button"
                style={{
                  minHeight: 44,
                  padding: "10px 20px",
                  borderRadius: 8,
                  border: "1px solid var(--cn-cascadia-tile-outline)",
                  background: "transparent",
                  cursor: "pointer",
                  fontSize: 13,
                  color: "var(--cn-cascadia-label)",
                }}
              >
                야생동물 건너뛰기 / Skip wildlife
              </button>
            </div>
          ) : null}
        </div>

        {/* Right rail: market + scoring cards + landmark panel */}
        <aside style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Market
            market={market}
            phase={turn.phase}
            activePlayerNatureTokens={activePlayerNatureTokens}
            selectedSlot={selectedSlot}
            onDraftPair={handleSelectDraftSlot}
            onWipeOptional={handleWipeOptional}
            onSpendNatureDecouple={handleSpendNatureDecouple}
            layout="rail"
          />

          <ScoringCardPanel scoringCards={scoringCards} />

          {/* LandmarkPanel — only when Landmarks module is on (spec §9) */}
          {ruleset.modules.landmarks &&
          turn.phase === "unlockLandmark" &&
          isMyTurn ? (
            <LandmarkPanel
              eligibleHabitats={eligibleHabitats}
              availableCards={[]}
              onUnlock={handleUnlockLandmark}
              onChooseCard={handleChooseLandmarkCard}
              onSkip={handleSkipLandmark}
              selectedHabitat={selectedLandmarkHabitat}
            />
          ) : null}
        </aside>
      </div>

      {/* Mobile: bottom-sheet market */}
      <div className="cascadia-mobile-market" style={{ display: "none" }}>
        <Market
          market={market}
          phase={turn.phase}
          activePlayerNatureTokens={activePlayerNatureTokens}
          selectedSlot={selectedSlot}
          onDraftPair={handleSelectDraftSlot}
          onWipeOptional={handleWipeOptional}
          onSpendNatureDecouple={handleSpendNatureDecouple}
          layout="sheet"
        />
      </div>

      {isGameOver && gameState.winners ? (
        <EndOfGameSheet
          players={players}
          winners={gameState.winners}
          onBackToLobby={handleBackToLobby}
        />
      ) : null}

      <style>{`
        @media (max-width: 980px) {
          .cascadia-game-layout {
            grid-template-columns: 1fr !important;
            gap: 12px !important;
            padding: 12px !important;
          }
        }
        @media (max-width: 768px) {
          .cascadia-game-layout aside:last-child {
            display: none !important;
          }
          .cascadia-mobile-market {
            display: block !important;
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            z-index: 20;
          }
        }
        @media (max-width: 480px) {
          .cascadia-game-layout {
            padding: 8px !important;
            gap: 8px !important;
          }
        }
      `}</style>
    </div>
  );
}
